/**
 * TODO: Combine common functionality between this and ValueViewString
 */

import copyToClipboard from 'copy-to-clipboard';
import isUrl from 'is-url';
import React, {ReactNode, useCallback, useState} from 'react';
import styled from 'styled-components';

import {toast} from '../../../../common/components/elements/Toast';
import Markdown from '../../../../common/components/Markdown';
import {TargetBlank} from '../../../../common/util/links';
import {Button} from '../../../Button';
import {CodeEditor} from '../../../CodeEditor';
import {TooltipHint} from '../../../DraggablePopups';
import {
  type CellType,
  useColumnOrLocalViewMode,
} from '../Browse3/columnViewMode';
import {MaybeReferenceUri} from '../Browse3/pages/CallPage/ValueViewReferenceUri';
import {CellValueWithPopupOver, TooltipContent} from './CellValueWithPopupOver';

const CollapsedWrapper = styled.div<{hasLineClamp?: boolean}>`
  overflow: hidden;
  text-overflow: ellipsis;
  ${props => !props.hasLineClamp && 'white-space: nowrap;'}
`;
CollapsedWrapper.displayName = 'S.CollapsedWrapper';

const TooltipText = styled.div<{isJSON: boolean}>`
  max-height: 35vh;
  overflow: auto;
  white-space: break-spaces;
  ${props => props.isJSON && 'font-family: monospace;'}
`;
TooltipText.displayName = 'S.TooltipText';

const isJSON = (value: string): boolean => {
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object') {
      return true;
    }
  } catch (err) {
    // ignore
  }
  return false;
};

const VIEW_MODES = ['Text', 'Code', 'Markdown'] as const;
export type ViewMode = (typeof VIEW_MODES)[number];

// Discriminates this cell's view-mode entries from other cell types sharing a
// column key (see `useColumnOrLocalViewMode`).
const CELL_TYPE: CellType = 'string';

type CellValueStringProps = {
  value: string;
  style?: React.CSSProperties;
  defaultViewMode?: ViewMode;
  /**
   * Column-wide view-mode key. Under a `ColumnViewModeProvider` the Text /
   * Code / Markdown choice is shared across the column; without one it's
   * per-cell local state.
   */
  columnKey?: string;
};

const MAX_DISPLAY_LENGTH = 500;
const MAX_CODE_VIEW_SIZE = 100 * 1024; // 100KB threshold for code view

const CellValueStringWithPopup = ({
  value,
  style,
  defaultViewMode = 'Text',
  columnKey,
}: CellValueStringProps) => {
  const trimmed = value.trim();
  const displayTrimmed =
    trimmed.length > MAX_DISPLAY_LENGTH
      ? trimmed.substring(0, MAX_DISPLAY_LENGTH) + '...'
      : trimmed;
  const json = isJSON(trimmed);

  // Check if payload is too large for code view (>100KB)
  const isLargePayload = trimmed.length > MAX_CODE_VIEW_SIZE;

  // Default to Text view for large payloads, even if defaultViewMode is 'Code'
  const initialViewMode: ViewMode =
    isLargePayload && defaultViewMode === 'Code'
      ? 'Text'
      : json
        ? 'Code'
        : defaultViewMode;

  const [viewMode, setViewMode] = useColumnOrLocalViewMode<ViewMode>(
    columnKey,
    CELL_TYPE,
    initialViewMode,
    VIEW_MODES
  );

  const copy = useCallback(() => {
    copyToClipboard(value);
    toast('Copied to clipboard');
  }, [value]);

  const [wrapLines, setWrapLines] = useState(false);

  // Check if line-clamp is being used in the style prop
  const hasLineClamp = style && 'WebkitLineClamp' in style;

  const collapsed = (
    <CollapsedWrapper style={style} hasLineClamp={hasLineClamp}>
      {displayTrimmed}
    </CollapsedWrapper>
  );

  const tooltip = (
    <TooltipContent>
      <TooltipText isJSON={json}>{displayTrimmed}</TooltipText>
      <TooltipHint>Click to view full content</TooltipHint>
    </TooltipContent>
  );

  const leftToolbarButtons = (
    <>
      <Button
        size="small"
        variant="ghost"
        active={viewMode === 'Text'}
        icon="text-language"
        tooltip="Text mode"
        onClick={e => {
          e.stopPropagation();
          setViewMode('Text');
        }}
      />
      <Button
        size="small"
        variant="ghost"
        active={viewMode === 'Markdown'}
        icon="markdown"
        tooltip="Markdown mode"
        onClick={e => {
          e.stopPropagation();
          setViewMode('Markdown');
        }}
      />
      <Button
        size="small"
        variant="ghost"
        active={viewMode === 'Code'}
        icon="code-alt"
        tooltip="Code mode"
        onClick={e => {
          e.stopPropagation();
          setViewMode('Code');
        }}
      />
    </>
  );

  const rightToolbarButtons = (
    <>
      {viewMode === 'Code' && (
        <>
          <Button
            size="small"
            variant="ghost"
            active={!wrapLines}
            icon="pan-tool-1"
            tooltip="Don't wrap code"
            onClick={() => {
              setWrapLines(false);
            }}
          />
          <Button
            className="mr-4"
            size="small"
            variant="ghost"
            active={wrapLines}
            icon="wrap"
            tooltip="Wrap code"
            onClick={() => {
              setWrapLines(true);
            }}
          />
        </>
      )}
      <Button
        size="small"
        variant="ghost"
        icon="copy"
        tooltip="Copy to clipboard"
        onClick={e => {
          e.stopPropagation();
          copy();
        }}
      />
    </>
  );

  let popoverBody: ReactNode = (
    <TooltipText isJSON={json}>{trimmed}</TooltipText>
  );
  if (viewMode === 'Code') {
    let language;
    let reformatted = trimmed;
    try {
      reformatted = JSON.stringify(JSON.parse(trimmed), null, 2);
      language = 'json';
    } catch (err) {
      // ignore
    }
    popoverBody = (
      <CodeEditor
        value={reformatted}
        language={language}
        readOnly
        handleMouseWheel
        alwaysConsumeMouseWheel={false}
        wrapLines={wrapLines}
      />
    );
  } else if (viewMode === 'Markdown') {
    popoverBody = <Markdown content={trimmed} />;
  }

  return (
    <CellValueWithPopupOver
      collapsed={collapsed}
      tooltip={tooltip}
      leftToolbarButtons={leftToolbarButtons}
      rightToolbarButtons={rightToolbarButtons}
      popoverBody={popoverBody}
    />
  );
};

export const CellValueString = ({
  value,
  style,
  defaultViewMode,
  columnKey,
}: CellValueStringProps) => {
  const trimmed = value.trim();
  return (
    <MaybeReferenceUri value={trimmed}>
      {isUrl(trimmed) ? (
        // The trace value is baked into the href, which session replay's default 'mask' level does not scrub, so hide the element from replay.
        <TargetBlank href={trimmed} data-dd-privacy="hidden">
          {trimmed}
        </TargetBlank>
      ) : (
        <CellValueStringWithPopup
          value={value}
          style={style}
          defaultViewMode={defaultViewMode}
          columnKey={columnKey}
        />
      )}
    </MaybeReferenceUri>
  );
};
